import { Component } from '@theme/component';
import { ResizeNotifier } from '@theme/utilities';
import { ThemeEvents, CustomSizeUpdateEvent, SizeSelectedEvent, UnitChangeEvent, CustomSizeError } from '@theme/events';

/**
 * SizeChart – metafield based size chart component
 * Listen size selection changes and dispatch custom events
 * @extends {Component}
 */
export default class SizeChart extends Component {
  #abortController = new AbortController();
  #sizeFields = ['Bust', 'Waist', 'Hips', 'Hollow_to_Hem', 'Height'];

  /**
   * @type {{
   *   range_of_sizes?: {
   *     [unit: string]: {
   *       [field: string]: { min: number; max: number }
   *     }
   *   };
   *   [key: string]: any; // Allow string indexing
   * }}
   */
  #sizeChartData;

  /** @type {string} */
  #currentUnit = 'inch';

  /** @type {string} */
  #currentSizeValue = 'US0';

  /** @type {HTMLInputElement} */
  #unitToggle;

  /** @type {number[]} */
  #showedSizeRowsIndexes = [0, 1, 2];

  /** @type {Element[]} */
  #sizeRows = [];

  /** @type {object & { [key: string]: any }} */
  #customSizeData = {
    Bust: '',
    Waist: '',
    Hips: '',
    Hollow_to_Hem: '',
    Height: '',
    Extra_Length: ''
  };

  /** @type {Element[]} */
  #customSizeInputs = [];

  /** @type {HTMLElement} */
  #measureTip;

  #resizeObserver = new ResizeNotifier(() => this.updateSizePickerCss());

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.sizeSelected, this.#onSizeSelected, { signal });

    // Initialize elements
    const unitToggle = this.querySelector('#sizeUnitToggle');
    if (unitToggle instanceof HTMLInputElement) {
      this.#unitToggle = unitToggle;
    }

    const sizeRows = Array.from(this.querySelectorAll('tr[data-size-row]'));
    this.#sizeRows = sizeRows;

    const customSizeInputs = Array.from(this.querySelectorAll('#customSizeContent input, #customSizeContent select'));
    this.#customSizeInputs = customSizeInputs;

    const measureTip = this.querySelector('#sizeMeasureTip');
    if (measureTip instanceof HTMLElement) {
      this.#measureTip = measureTip;
    }

    // Get size chart data from script application/json tag
    this.#sizeChartData = this.getSizeChartData();

    // Initial custom size inputs placeholder
    this.initialInputsPlaceholder();

    this.#unitToggle.addEventListener('change', this.onUnitToggleChange.bind(this));
    const customSizeWrapper = this.querySelector('#customSizeContent');
    if (customSizeWrapper) customSizeWrapper.addEventListener('change', this.onSizeChange.bind(this));
    this.#customSizeInputs.forEach(input => input.addEventListener('focus', this.updateMeaureTips.bind(this)));
    this.#customSizeInputs.forEach(input => input.addEventListener('blur', this.checkValidate.bind(this)));

    this.#resizeObserver.observe(this);
    this.updateSizePickerCss();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Unit toggle change handler
   * @param {Event} event
   */
  onUnitToggleChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    this.#currentUnit = target.checked ? 'cm' : 'inch';
    this.setAttribute('data-unit', this.#currentUnit);

    this.initialInputsPlaceholder();

    try {
      const form = document.querySelector('product-form-component');
      if (form) this.dispatchEvent(new UnitChangeEvent({ unit: this.#currentUnit }));

      const inputs = this.querySelectorAll('#customSizeContent input, #customSizeContent select');
      inputs.forEach(input => {
        if (input instanceof HTMLInputElement && input.type === 'number') {
          input.classList.remove('has_error');
          const message = input.querySelector('.validate-error');
          if (message) message.textContent = '';
          input.value = '';
        } else if (input instanceof HTMLSelectElement) {
          input.value = '';
        } else if (input instanceof HTMLInputElement && input.type === 'checkbox') {
          input.checked = false;
        }
      });

      this.#sizeFields.forEach(key => {
        this.#customSizeData[key] = '';
      });
    }
    catch(error) {
      console.error(error.message);
    }
  }

  /**
   * Handle size change
   * @param {Event} event
   */
  onSizeChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    // ✅ policy checkbox
    if (target instanceof HTMLInputElement && target.type === 'checkbox') {
      const isValidate = this.checkCustomSizeInputs();
      if (isValidate && target.checked) {
        const { Bust, Waist, Hips, Hollow_to_Hem, Height, Extra_Length } = this.#customSizeData;
        this.dispatchEvent(
          new CustomSizeUpdateEvent({
            Bust, Waist, Hips, Hollow_to_Hem, Height, Extra_Length
          })
        );
      } else {
        this.dispatchEvent(new CustomSizeError({ message: 'Please agree to the relevant policies.' }));
      }
      return;
    }

    // ✅ Bust, Waist, Hips, Height, Hollow_to_Hem, Extra_Length
    if (target instanceof HTMLInputElement && target.type === 'number') {
      const result = this.validateTool(target);
      if (result) {
        this.#customSizeData[target.name] = Number(target.value);
      } else {
        this.dispatchEvent(new CustomSizeError({ message: `${target.name}: ${target.value} is out of range.` }));
      }
    }

    // ✅ select（Extra_Length）
    if (target instanceof HTMLSelectElement) {
      this.#customSizeData[target.name] = target.value;
    }

    if (this.checkCustomSizeInputs()) {
      const { Bust, Waist, Hips, Hollow_to_Hem, Height, Extra_Length } = this.#customSizeData;
      this.dispatchEvent(
        new CustomSizeUpdateEvent({
          Bust, Waist, Hips, Hollow_to_Hem, Height, Extra_Length
        })
      );
    }
  }

  checkCustomSizeInputs() {
    const policy = /** @type {HTMLInputElement} */ (this.querySelector('#user_custom_policy'));
    if (policy && !policy.checked) return false;

    const ranges = this.#sizeChartData?.range_of_sizes?.[this.#currentUnit];
    if (!ranges) return false;

    for (const key of this.#sizeFields) {
      const value = Number(this.#customSizeData[key]);
      if (Number.isNaN(value)) return false;

      const range = ranges[key];
      if (!range) return false;

      if (value < range.min || value > range.max) {
        return false;
      }
    }

    return true;
  }

  /**
   * Handle size selected event from other components
   * @param {SizeSelectedEvent} event
   */
  #onSizeSelected = (event) => {
    const {size} = event.detail.resource;
    if (!size) return;

    const customSizeContent = this.querySelector('#customSizeContent');
    const sizeTable = this.querySelector('#sizeChartTable');

    if (this.#currentSizeValue.toLocaleLowerCase() !== 'custom size' && size.toLowerCase() === 'custom size') {
      // Show custom size inputs, hide size table
      if (customSizeContent instanceof HTMLElement && sizeTable instanceof HTMLElement) {
        customSizeContent.style.display = 'block';
        sizeTable.style.display = 'none';
      }
    } else if (this.#currentSizeValue.toLocaleLowerCase() === 'custom size' && size.toLowerCase() !== 'custom size') {
      // Hide custom size inputs, show size table
      if (customSizeContent instanceof HTMLElement && sizeTable instanceof HTMLElement) {
        customSizeContent.style.display = 'none';
        sizeTable.style.display = 'table';
      }

      // Clear this.#customSizeData
      try {
        const inputs = this.querySelectorAll('#customSizeContent input, #customSizeContent select');
        inputs.forEach(input => {
          if (input instanceof HTMLInputElement && input.type === 'number') {
            input.classList.remove('has_error');
            const message = input.querySelector('.validate-error');
            if (message) message.textContent = '';
            input.value = '';
          } else if (input instanceof HTMLSelectElement) {
            input.value = '';
          } else if (input instanceof HTMLInputElement && input.type === 'checkbox') {
            input.checked = false;
          }
        });

        this.#sizeFields.forEach(key => {
          this.#customSizeData[key] = '';
        });
      }
      catch(error) {
        console.error(error.message);
      }
      this.updateShowedSizeRowsIndexes(size);
    } else {
      this.updateShowedSizeRowsIndexes(size);
    }
    this.#currentSizeValue = size;
  }

  /**
   * Update showed size rows indexes
   * @param {string} sizeName
   */
  updateShowedSizeRowsIndexes(sizeName) {
    this.querySelector(`#sizeChartTable [data-size-name="${this.#currentSizeValue}"]`)?.classList.remove('is-active');
    this.querySelector(`#sizeChartTable [data-size-name="${sizeName}"]`)?.classList.add('is-active');

    const [prevIndex, activedIndex, afterIndex] = this.#showedSizeRowsIndexes;
    if (typeof prevIndex === 'number') this.#sizeRows[prevIndex]?.setAttribute('data-displayed', 'false');
    if (typeof activedIndex === 'number') this.#sizeRows[activedIndex]?.setAttribute('data-displayed', 'false');
    if (typeof afterIndex === 'number') this.#sizeRows[afterIndex]?.setAttribute('data-displayed', 'false');

    /** @type number[] */
    let showedSizeRowsIndexes = [];
    const currentIndex = this.#sizeRows.findIndex(row => row.getAttribute('data-size-name') === sizeName);
    if (currentIndex !== -1) {
      // Get prev row index and after row index according to currentIndex
      if (currentIndex === 0) {
        showedSizeRowsIndexes = [0, 1, 2];
      } else if (currentIndex >= (this.#sizeRows.length - 1)) {
        showedSizeRowsIndexes = [currentIndex - 2, currentIndex - 1, currentIndex];
      } else {
        showedSizeRowsIndexes = [currentIndex - 1, currentIndex, currentIndex + 1];
      }
    }
    this.#showedSizeRowsIndexes = showedSizeRowsIndexes;

    const [newPrevIndex, newActivedIndex, newAfterIndex] = showedSizeRowsIndexes;

    if (typeof newPrevIndex === 'number') this.#sizeRows[newPrevIndex]?.setAttribute('data-displayed', 'true');
    if (typeof newActivedIndex === 'number') this.#sizeRows[newActivedIndex]?.setAttribute('data-displayed', 'true');
    if (typeof newAfterIndex === 'number') this.#sizeRows[newAfterIndex]?.setAttribute('data-displayed', 'true');
  }

  /**
   * @param {string} inputName
   */
  initialInputsPlaceholder(inputName = '') {
    if (this.#currentUnit === 'inch') {
      const inputRanges = this.#sizeChartData?.range_of_sizes?.inch;
      if (inputRanges) {
        for (const [key, value] of Object.entries(inputRanges)) {
          const placeholder = `${value.min} - ${value.max}`;
          this.querySelector(`input[name="${key}"]`)?.setAttribute('placeholder', placeholder);
        }
      }
    } else {
      const inputRanges = this.#sizeChartData?.range_of_sizes?.cm;
      if (inputRanges) {
        for (const [key, value] of Object.entries(inputRanges)) {
          const placeholder = `${value.min} - ${value.max}`;
          this.querySelector(`input[name="${key}"]`)?.setAttribute('placeholder', placeholder);
        }
      }
    }
  }

  /**
   * @param {Event} event
   */
  updateMeaureTips(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target instanceof HTMLInputElement && target.type === 'checkbox') return;

    try {
      const parent = target.closest('.form-row');
      parent?.classList.remove('has_error');
      const message = parent?.querySelector('.validate-error');
      if (message) message.textContent = '';
    }
    catch(error) {
      console.error(error.message);
      return;
    }

    const inputName = /** @type {string} */ (target.getAttribute('name'));
    if (!inputName || !this.#sizeChartData) return;
    const descString = this.#sizeChartData[inputName];
    if (descString) {
      this.#measureTip.innerHTML = `<strong>${inputName}: </strong>${descString}`;
    } else {
      this.#measureTip.innerHTML = `<strong>${inputName}</strong>`;
    }
  }

  /**
   * @param {Event} event
   */
  checkValidate(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target instanceof HTMLInputElement && target.type === 'checkbox') return;

    const result = this.validateTool(target);
    if (!result) this.dispatchEvent(new CustomSizeError({ message: `${target.name}: ${target.value} is out of range.` }));
  }

  /**
   * @param {HTMLInputElement} input 
   */
  validateTool(input) {
    if (!(input instanceof HTMLInputElement)) return false;

    const rawValue = input.value.trim();
    if (!rawValue) {
      return false;
    }

    const inputValue = Number(rawValue);
    if (Number.isNaN(inputValue)) return false;
    const inputName = input.getAttribute('name');
    if (!inputName) return false;
    const ranges = this.#sizeChartData?.range_of_sizes;
    if (!ranges) return false;
    if (this.#currentUnit === 'inch') {
      const value = ranges.inch?.[inputName];
      if (!value || inputValue < value.min || inputValue > value.max) {
        const parent = input.closest('.form-row');
        parent?.classList.add('has_error');
        const message = parent?.querySelector('.validate-error');
        if (message) message.textContent = `${inputValue} out of range: ${value?.min} - ${value?.max}`;

        return false;
      }
    } else {
      const value = ranges.cm?.[inputName];
      if (!value || inputValue < value.min || inputValue > value.max) {
        const parent = input.closest('.form-row');
        parent?.classList.add('has_error');
        const message = parent?.querySelector('.validate-error');
        if (message) message.textContent = `${inputValue} out of range: ${value?.min} - ${value?.max}`;

        return false;
      }
    }

    return true;
  }

  /**
   * Update pill animation CSS
   */
  updateSizePickerCss() {}

  updateFieldsetCss() {
    this.updateSizePickerCss();
  }

  getSizeChartData() {
    const dataScript = this.querySelector('script[data-size-chart-data]');
    if (!dataScript?.textContent) return null;

    try {
      return JSON.parse(dataScript.textContent);
    } catch (e) {
      console.error('[SizeChart] JSON parse failed', e);
      return null;
    }
  }
}

if (!customElements.get('size-chart-component')) {
  customElements.define('size-chart-component', SizeChart);
}
